import { PopupAdapter } from '../types';

export class CustomPopupAdapter implements PopupAdapter {
  private id: string;
  private formSelector: string;
  private shouldShow: () => Promise<boolean>;
  private afterShown: () => Promise<void>;
  private afterHidden: () => Promise<void>;
  private forceShow: boolean;

  constructor({
    id,
    formSelector,
    shouldShow,
    afterShown,
    afterHidden,
    forceShow,
  }: {
    id: string;
    formSelector: string;
    shouldShow?: () => Promise<boolean>;
    afterShown?: () => Promise<void>;
    afterHidden?: () => Promise<void>;
    forceShow?: boolean;
  }) {
    this.id = id;
    this.formSelector = formSelector;
    this.shouldShow = shouldShow ?? (() => Promise.resolve(true));
    this.afterShown = afterShown ?? (() => Promise.resolve());
    this.afterHidden = afterHidden ?? (() => Promise.resolve());

    this.forceShow = typeof forceShow === 'undefined' ? false : forceShow;
  }

  getRootElement(): HTMLDivElement | null {
    const startingElement = document.querySelector(this.formSelector);

    if (!startingElement) {
      return null;
    }

    // Scan all the way back up the tree until the parentElement is body
    let rootElement: HTMLDivElement = startingElement as HTMLDivElement;
    while (rootElement.parentElement && rootElement.parentElement.tagName !== 'BODY') {
      rootElement = rootElement.parentElement as HTMLDivElement;
    }

    return rootElement;
  }

  async show(): Promise<boolean> {
    if (!this.forceShow) {
      const shouldShow = await this.shouldShow();
      if (!shouldShow) return false;
    }

    const element = this.getRootElement();

    if (!element) return false;

    element.style.removeProperty('display');
    element.style.setProperty('display', 'block', 'important');

    await this.afterShown();

    return true;
  }

  async hide(): Promise<boolean> {
    const element = this.getRootElement();

    if (!element) return true;

    element.style.setProperty('display', 'none', 'important');

    await this.afterHidden();

    return true;
  }

  isVisible(): boolean {
    const element = this.getRootElement();
    return !!element;
  }

  getId(): string {
    return this.id;
  }

  getForceShow(): boolean {
    return this.forceShow;
  }

  private_hasCloseButton(element: HTMLElement): boolean {
    const closeSelectors = [
      'button.close',
      '.close',
      '.close-button',
      '.closeButton',
      '[aria-label="Close"]',
      '[aria-label="close"]',
      'button:only-child',
      'button:contains("Close")',
      'button:contains("×")',
      'button:contains("✕")',
    ];
    const buttons = element.querySelectorAll('button, a, [role="button"]');
    for (const button of Array.from(buttons)) {
      const rect = button.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const isInTopRight = rect.right > elementRect.right - 50 && rect.top < elementRect.top + 50;
      if (isInTopRight) return true;
      const buttonText = button.textContent?.trim();
      if (
        buttonText === '×' ||
        buttonText === '✕' ||
        buttonText === 'Close' ||
        buttonText === 'close' ||
        buttonText === 'X'
      ) {
        return true;
      }
    }
    for (const selector of closeSelectors) {
      try {
        if (element.querySelector(selector)) return true;
      } catch (e) {}
    }
    return false;
  }

  private_checkForPopup(element: HTMLElement): boolean {
    if (element.offsetWidth < 100 || element.offsetHeight < 100) return false;
    const style = window.getComputedStyle(element);
    const isPopupLike =
      (style.position === 'fixed' || style.position === 'absolute') &&
      parseInt(style.zIndex, 10) > 10 &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      style.opacity !== '0' &&
      (element.offsetWidth > window.innerWidth * 0.5 ||
        element.offsetHeight > window.innerHeight * 0.5 ||
        style.top.includes('50%') ||
        style.left.includes('50%') ||
        style.transform.includes('translate') ||
        (element.getBoundingClientRect().top > window.innerHeight * 0.1 &&
          element.getBoundingClientRect().left > window.innerWidth * 0.1 &&
          element.getBoundingClientRect().right < window.innerWidth * 0.9 &&
          element.getBoundingClientRect().bottom < window.innerHeight * 0.9));
    if (isPopupLike) {
      if (this.private_hasCloseButton(element)) {
        return true;
      }
    }
    return false;
  }

  static forceHideAll(): void {
    console.debug('CustomPopupAdapter: forceHideAll applied.');
  }
}

//@ts-ignore
window.CustomPopupAdapter = CustomPopupAdapter;
